import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import {
  Box,
  Flex,
  Popover,
  type PopoverProps,
  Text,
  Tooltip,
} from '@mantine/core'
import { useRouter } from 'next/router'
import type { FC } from 'react'

import { Trans, useTranslation } from '@flecto/translations'
import {
  type ParameterID,
  type CoreBundleItem,
  type CoreProduct,
} from '@flecto/types'

import { AvatarItem, BadgeProductInsurance, Icon } from '..'
import classes from './styles.module.css'

interface QuantityProps {
  bi: CoreBundleItem
  withStock: boolean
}

const Quantity: FC<QuantityProps> = ({ bi, withStock }) => {
  const { t } = useTranslation('shared', { keyPrefix: 'shared' })

  return (
    <>
      <Text className={classes.quantity} ta='right'>
        x{bi.quantity}
      </Text>
      {withStock && (
        <Text c='dimmed' size='sm' className={classes.availability}>
          {bi.item.item_attributes?.quantity} {t('in_stock_items')}
        </Text>
      )}
    </>
  )
}

interface BundleItemsProps {
  product: CoreProduct
  withStock: boolean
  shouldShowInsuranceBadge: boolean
  handleItemClick: (id: ParameterID) => void
}

const BundleItems: FC<BundleItemsProps> = ({
  product,
  withStock,
  shouldShowInsuranceBadge,
  handleItemClick,
}) => (
  <>
    {product.bundle_attributes?.bundle_items?.map((bi) => (
      <Box
        key={bi.id}
        className={classes.item}
        onClick={() => handleItemClick(bi.item.id)}
        data-testid='popover-item'
      >
        <AvatarItem item={bi.item} className={classes.avatar} />

        <Flex className={classes.title}>
          <Text fw={500}>{bi.item.title}</Text>

          {shouldShowInsuranceBadge && (
            <BadgeProductInsurance
              product={product}
              data-testid='badge-product-insurance'
            />
          )}
        </Flex>

        <Flex align='center'>
          <Text size='sm' c='dimmed' className={classes.id}>
            ID: {bi.item.internal_id}
          </Text>
          {bi.item.item_attributes?.quantity_available === 0 && (
            <>
              <Text size='sm' c='dimmed' ml='xs' mr='xs'>
                ·
              </Text>
              <Text size='sm' c='orange.7'>
                <Trans ns='saas' i18nKey='items.out_of_stock' />
              </Text>
              <Tooltip
                label={
                  <Text ta='center'>
                    <Trans
                      ns='saas'
                      i18nKey='items.out_of_stock'
                      components={{ 1: <br /> }}
                    />
                  </Text>
                }
              >
                <Box>
                  <Icon
                    ml='xs'
                    size='xs'
                    color='orange.7'
                    icon={faInfoCircle}
                  />
                </Box>
              </Tooltip>
            </>
          )}
        </Flex>

        <Quantity bi={bi} withStock={withStock} />
      </Box>
    ))}
  </>
)

export type PopoverProductBundleItemsParams = PopoverProps & {
  product: CoreProduct
  withInsuranceBadge?: boolean
  withStock?: boolean
  onItemClick?: (itemId: ParameterID) => void
}

export const PopoverProductBundleItems: FC<PopoverProductBundleItemsParams> = ({
  product,
  children,
  onItemClick,
  withInsuranceBadge,
  withStock = false,
  ...props
}) => {
  const router = useRouter()

  if (!product?.bundle) return <>{children}</>

  const shouldShowInsuranceBadge = !!withInsuranceBadge && product.insured

  const handleItemClick = (itemId: ParameterID) => {
    if (onItemClick) {
      onItemClick(itemId)
    } else {
      router.push('/items')
    }
  }

  return (
    <Popover position='bottom' width={330} withinPortal {...props}>
      <Popover.Target>
        <Box data-testid='popover-target' className={classes.target}>
          {children}
        </Box>
      </Popover.Target>

      <Popover.Dropdown data-testid='popover-dropdown'>
        <BundleItems
          product={product}
          withStock={withStock}
          shouldShowInsuranceBadge={shouldShowInsuranceBadge}
          handleItemClick={handleItemClick}
        />
      </Popover.Dropdown>
    </Popover>
  )
}

export default PopoverProductBundleItems
